import { useState, useCallback } from 'react';
import {
  HumanMean,
  HumanMeanResponse,
  SearchHumanMeansRequest,
  ACLAuthorizationRequest,
  SearchHumanMeansPostContentTypeEnum,
  CreateHumanMeanContentTypeEnum,
} from '@/api/bol';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { getHumanMeansApi } from '@/services/bolApiClient';
import { extractErrorMessage, formatOperationError } from '@/utils/errorUtils';
import { getLogisticsObjectDetailPath } from '@/types/logisticsObject';
import { getEffectiveLogisticsObjectSearchQuery } from '@/utils/searchDefaults';

export interface UseHumanMeansReturn {
  humanMeans: HumanMeanResponse[];
  loading: boolean;
  searchError: string | null;
  selectedHumanMean: HumanMeanResponse | null;
  setSelectedHumanMean: (humanMean: HumanMeanResponse | null) => void;
  fetchHumanMeans: (page?: number, pageSize?: number, searchBody?: SearchHumanMeansRequest) => Promise<boolean>;
  createHumanMean: (humanMeanData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => Promise<void>;
  deleteHumanMean: (humanMeanId: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const useHumanMeans = (): UseHumanMeansReturn => {
  const [items, setItems] = useState<HumanMeanResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedHumanMean, setSelectedHumanMean] = useState<HumanMeanResponse | null>(null);
  const [total, setTotal] = useState(0);

  const { showSuccess, showError } = useSnackbarNotification();

  const fetchHumanMeans = useCallback(async (page: number = 0, pageSize: number = 10, searchBody?: SearchHumanMeansRequest): Promise<boolean> => {
    const normalizedSearchBody = getEffectiveLogisticsObjectSearchQuery(searchBody);
    setLoading(true);
    setSearchError(null);

    try {
      const api = getHumanMeansApi();
      const response = await api.searchHumanMeansPost({
        contentType: SearchHumanMeansPostContentTypeEnum.ApplicationJson,
        searchHumanMeansRequest: normalizedSearchBody,
        limit: pageSize,
        offset: page * pageSize,
      });

      setItems(response.results || []);
      setTotal(response.pagination?.total || 0);
      return true;
    } catch (err: unknown) {
      const errorMessage = await extractErrorMessage(err, 'Failed to fetch human-means');
      setSearchError(errorMessage);
      showError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const createHumanMean = useCallback(async (humanMeanData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => {
    try {
      const api = getHumanMeansApi();

      const createdHumanMean = await api.createHumanMean({
        contentType: CreateHumanMeanContentTypeEnum.ApplicationJson,
        xBOLMemberIdentifier: memberIdentifier,
        humanMean: humanMeanData as HumanMean,
        source: true,
        xBOLACLAuthorizations: aclAuthorizations ? JSON.stringify(aclAuthorizations) : undefined,
      });

      const createdHumanMeanId = createdHumanMean?.id;

      showSuccess(
        'HumanMean created successfully',
        createdHumanMeanId
          ? {
              link: {
                label: 'View details',
                href: getLogisticsObjectDetailPath('human-mean', createdHumanMeanId),
              },
            }
          : undefined,
      );
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Create human-mean', 'Failed to create human-mean');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  const deleteHumanMean = useCallback(async (humanMeanId: string) => {
    try {
      const api = getHumanMeansApi();
      await api.deleteHumanMeanById({ humanMeanId });
      showSuccess('HumanMean deleted successfully');
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Delete human-mean', 'Failed to delete human-mean');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  return {
    humanMeans: items,
    loading,
    searchError,
    selectedHumanMean,
    setSelectedHumanMean,
    fetchHumanMeans,
    createHumanMean,
    deleteHumanMean,
    setTotal,
    total,
  };
};
